import { useCallback, useEffect, useRef, useState } from 'react';
import { useMotionPreference } from '../../hooks/useMotionPreference';
import './InteractiveMascot.css';

const FALLBACK_ROOT = '/mascot/fallback';
const fallbackFrames = {
  center: `${FALLBACK_ROOT}/center.webp`,
  left: `${FALLBACK_ROOT}/left.webp`,
  right: `${FALLBACK_ROOT}/right.webp`,
  up: `${FALLBACK_ROOT}/up.webp`,
  down: `${FALLBACK_ROOT}/down.webp`,
  'up-left': `${FALLBACK_ROOT}/up-left.webp`,
  'up-right': `${FALLBACK_ROOT}/up-right.webp`,
  'down-left': `${FALLBACK_ROOT}/down-left.webp`,
  'down-right': `${FALLBACK_ROOT}/down-right.webp`,
};

const layeredAssets = {
  body: '/mascot/body.webp',
  head: '/mascot/head.webp',
  eyeLeft: '/mascot/eye-left.webp',
  pupilLeft: '/mascot/pupil-left.webp',
  eyeRight: '/mascot/eye-right.webp',
  pupilRight: '/mascot/pupil-right.webp',
  blink: '/mascot/blink.webp',
  cloud: '/mascot/cloud.webp',
};

// This manifest reflects files verified in public/mascot on this branch. Keep
// missing layers disabled so the browser never requests assets that do not exist.
const availableAssets = {
  layered: new Set(),
  expressions: new Set(),
};

const expressionAssets = {
  'mouth-neutral': '/mascot/expressions/mouth-neutral.webp',
  'mouth-smile': '/mascot/expressions/mouth-smile.webp',
  'mouth-laugh': '/mascot/expressions/mouth-laugh.webp',
  'mouth-smirk': '/mascot/expressions/mouth-smirk.webp',
  'mouth-surprised': '/mascot/expressions/mouth-surprised.webp',
  'mouth-confused': '/mascot/expressions/mouth-confused.webp',
  'eyes-open': '/mascot/expressions/eyes-open.webp',
  'eyes-happy': '/mascot/expressions/eyes-happy.webp',
  'eyes-surprised': '/mascot/expressions/eyes-surprised.webp',
  'eyes-confused': '/mascot/expressions/eyes-confused.webp',
  'eyes-laugh-closed': '/mascot/expressions/eyes-laugh-closed.webp',
  'brows-neutral': '/mascot/expressions/brows-neutral.webp',
  'brows-raised': '/mascot/expressions/brows-raised.webp',
  'brows-smirk': '/mascot/expressions/brows-smirk.webp',
  blink: '/mascot/expressions/blink.webp',
  'cheeks-happy': '/mascot/expressions/cheeks-happy.webp',
  'sweat-drop': '/mascot/expressions/sweat-drop.webp',
  'reaction-star': '/mascot/expressions/reaction-star.webp',
};

const expressionLayers = {
  neutral: { eyes: 'eyes-open', brows: 'brows-neutral', mouth: 'mouth-neutral' },
  smile: { eyes: 'eyes-open', brows: 'brows-neutral', mouth: 'mouth-smile' },
  laugh: { eyes: 'eyes-laugh-closed', eyesFallback: 'eyes-happy', brows: 'brows-neutral', mouth: 'mouth-laugh', cheeks: 'cheeks-happy' },
  smirk: { eyes: 'eyes-open', brows: 'brows-smirk', mouth: 'mouth-smirk' },
  surprised: { eyes: 'eyes-surprised', brows: 'brows-raised', mouth: 'mouth-surprised', effect: 'reaction-star' },
  confused: { eyes: 'eyes-confused', brows: 'brows-raised', mouth: 'mouth-confused', effect: 'sweat-drop' },
};

const mascotMessages = [
  'kubectl ready. Coffee pending.',
  'I only panic in non-production.',
  'Infrastructure should be boring — in a good way.',
  'This click has been successfully deployed.',
  'YAML is my cardio.',
];

const reactions = [
  { type: 'laugh', messageIndexes: [0, 4] },
  { type: 'smirk', messageIndexes: [1, 2] },
  { type: 'surprised', messageIndexes: [3] },
  { type: 'confused', messageIndexes: [0, 2, 4] },
];

const clamp = (value, minimum = -1, maximum = 1) => Math.min(maximum, Math.max(minimum, value));

function directionFor(x, y) {
  if (Math.hypot(x, y) < 0.2) return 'center';
  const horizontal = x < -0.28 ? 'left' : x > 0.28 ? 'right' : '';
  const vertical = y < -0.28 ? 'up' : y > 0.28 ? 'down' : '';
  return vertical && horizontal ? `${vertical}-${horizontal}` : vertical || horizontal || 'center';
}

function randomItem(items) {
  return items[Math.floor(Math.random() * items.length)];
}

export default function InteractiveMascot() {
  const rootRef = useRef(null);
  const headRef = useRef(null);
  const headImageRef = useRef(null);
  const alternateHeadImageRef = useRef(null);
  const activeHeadImageRef = useRef(0);
  const pupilLeftRef = useRef(null);
  const pupilRightRef = useRef(null);
  const motionRef = useRef({ targetX: 0, targetY: 0, currentX: 0, currentY: 0 });
  const faceCenterRef = useRef({ x: 0, y: 0 });
  const lastDirectionRef = useRef('center');
  const lastReactionRef = useRef(null);
  const lastMessageRef = useRef(-1);
  const timersRef = useRef([]);
  const [blinking, setBlinking] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [reaction, setReaction] = useState(null);
  const [bubble, setBubble] = useState(null);
  const reducedMotion = useMotionPreference();
  const useLayeredAssets = Object.keys(layeredAssets).every((key) => availableAssets.layered.has(key));

  const clearTimers = useCallback(() => {
    timersRef.current.forEach(window.clearTimeout);
    timersRef.current = [];
  }, []);

  useEffect(() => {
    if (!useLayeredAssets) {
      Object.values(fallbackFrames).forEach((source) => {
        const image = new Image();
        image.src = source;
      });
    }
  }, [useLayeredAssets]);

  useEffect(() => {
    let blinkTimer;
    let closeTimer;
    let cancelled = false;

    const scheduleBlink = () => {
      blinkTimer = window.setTimeout(() => {
        if (cancelled) return;
        setBlinking(true);
        closeTimer = window.setTimeout(() => {
          setBlinking(false);
          scheduleBlink();
        }, 120 + Math.random() * 40);
      }, 4000 + Math.random() * 4000);
    };

    scheduleBlink();
    return () => {
      cancelled = true;
      window.clearTimeout(blinkTimer);
      window.clearTimeout(closeTimer);
    };
  }, []);

  useEffect(() => {
    const finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
    const shouldTrack = finePointer.matches && !reducedMotion;
    let animationFrame;

    const refreshBounds = () => {
      if (!rootRef.current) return;
      const bounds = rootRef.current.getBoundingClientRect();
      faceCenterRef.current = {
        x: bounds.left + bounds.width * 0.5,
        y: bounds.top + bounds.height * 0.31,
      };
    };

    const showFallbackDirection = (direction) => {
      if (useLayeredAssets || direction === lastDirectionRef.current) return;
      const images = [headImageRef.current, alternateHeadImageRef.current];
      const currentIndex = activeHeadImageRef.current;
      const nextIndex = currentIndex === 0 ? 1 : 0;
      const currentImage = images[currentIndex];
      const nextImage = images[nextIndex];
      if (!currentImage || !nextImage) return;

      nextImage.src = fallbackFrames[direction];
      nextImage.classList.add('is-active');
      currentImage.classList.remove('is-active');
      activeHeadImageRef.current = nextIndex;
      lastDirectionRef.current = direction;
    };

    const resetTarget = () => {
      motionRef.current.targetX = 0;
      motionRef.current.targetY = 0;
      showFallbackDirection('center');
    };

    const onMouseMove = (event) => {
      if (!shouldTrack) return;
      const { x, y } = faceCenterRef.current;
      const deltaX = event.clientX - x;
      const deltaY = event.clientY - y;
      const distance = Math.hypot(deltaX, deltaY);
      const responseRange = Math.max(240, Math.min(window.innerWidth, window.innerHeight) * 0.75);
      const proximity = 1 - clamp(distance / responseRange, 0, 1);
      const responseStrength = 0.38 + proximity * 0.62;
      const targetX = distance > 1 ? clamp((deltaX / distance) * responseStrength) : 0;
      const targetY = distance > 1 ? clamp((deltaY / distance) * responseStrength) : 0;
      motionRef.current.targetX = targetX;
      motionRef.current.targetY = targetY;

      if (!useLayeredAssets) {
        const direction = directionFor(targetX, targetY);
        showFallbackDirection(direction);
      }
    };

    const onWindowExit = (event) => {
      if (!event.relatedTarget) resetTarget();
    };

    const animate = () => {
      const motion = motionRef.current;
      const targetX = motion.targetX;
      const targetY = motion.targetY;
      const easing = targetX === 0 && targetY === 0 ? 0.14 : 0.08;
      motion.currentX += (targetX - motion.currentX) * easing;
      motion.currentY += (targetY - motion.currentY) * easing;

      if (headRef.current) {
        headRef.current.style.setProperty('--mascot-head-x', `${motion.currentX * 2}px`);
        headRef.current.style.setProperty('--mascot-head-y', `${motion.currentY * 2}px`);
        headRef.current.style.setProperty('--mascot-head-rotate-x', `${motion.currentY * -5}deg`);
        headRef.current.style.setProperty('--mascot-head-rotate-y', `${motion.currentX * 7}deg`);
      }

      const pupilX = `${motion.currentX * 5}px`;
      const pupilY = `${motion.currentY * 4}px`;
      pupilLeftRef.current?.style.setProperty('transform', `translate3d(${pupilX}, ${pupilY}, 0)`);
      pupilRightRef.current?.style.setProperty('transform', `translate3d(${pupilX}, ${pupilY}, 0)`);
      animationFrame = window.requestAnimationFrame(animate);
    };

    refreshBounds();
    const resizeObserver = new ResizeObserver(refreshBounds);
    if (rootRef.current) resizeObserver.observe(rootRef.current);
    window.addEventListener('resize', refreshBounds, { passive: true });
    if (shouldTrack) {
      window.addEventListener('mousemove', onMouseMove, { passive: true });
      document.documentElement.addEventListener('mouseout', onWindowExit);
      animationFrame = window.requestAnimationFrame(animate);
    } else {
      resetTarget();
    }

    return () => {
      if (animationFrame) window.cancelAnimationFrame(animationFrame);
      resizeObserver.disconnect();
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('resize', refreshBounds);
      document.documentElement.removeEventListener('mouseout', onWindowExit);
    };
  }, [reducedMotion, useLayeredAssets]);

  useEffect(() => () => clearTimers(), [clearTimers]);

  const react = () => {
    clearTimers();
    const availableReactions = reactions.filter((item) => item.type !== lastReactionRef.current);
    const nextReaction = randomItem(availableReactions);
    const availableMessages = nextReaction.messageIndexes.filter((index) => index !== lastMessageRef.current);
    const messageIndex = randomItem(availableMessages.length ? availableMessages : nextReaction.messageIndexes);

    lastReactionRef.current = nextReaction.type;
    lastMessageRef.current = messageIndex;
    setReaction(nextReaction.type);
    setBubble({ message: mascotMessages[messageIndex], fading: false });

    timersRef.current.push(window.setTimeout(() => setReaction(null), reducedMotion ? 250 : 800));
    timersRef.current.push(window.setTimeout(() => {
      setBubble((current) => current ? { ...current, fading: true } : null);
    }, 2450));
    timersRef.current.push(window.setTimeout(() => setBubble(null), 2850));
  };

  const expressionName = reaction || (hovered ? 'smile' : 'neutral');
  const selectedExpression = expressionLayers[expressionName] || expressionLayers.neutral;
  const availableExpression = (primary, fallback) => {
    const key = availableAssets.expressions.has(primary) ? primary : fallback;
    return key && availableAssets.expressions.has(key) ? expressionAssets[key] : null;
  };
  const activeEyes = availableExpression(selectedExpression.eyes, selectedExpression.eyesFallback);
  const activeBrows = availableExpression(selectedExpression.brows);
  const activeMouth = availableExpression(selectedExpression.mouth);
  const activeCheeks = availableExpression(selectedExpression.cheeks);
  const activeEffect = availableExpression(selectedExpression.effect);
  const activeBlink = availableExpression('blink');
  const pupilsVisible = !blinking && ['neutral', 'smile', 'smirk'].includes(expressionName);

  return (
    <div ref={rootRef} className="interactive-mascot">
      <span
        className={`interactive-mascot__bubble${bubble?.fading ? ' is-fading' : ''}`}
        role="status"
        aria-live="polite"
      >
        {bubble?.message}
      </span>
      <button
        type="button"
        className="interactive-mascot__button"
        aria-label="Interact with site mascot"
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onFocus={() => setHovered(true)}
        onBlur={() => setHovered(false)}
        onClick={react}
      >
        <span className="interactive-mascot__stage" aria-hidden="true">
          <span className={`interactive-mascot__reaction${reaction ? ` is-${reaction}` : ''}`}>
            {useLayeredAssets ? (
              <>
                  <img className="interactive-mascot__layer" src={layeredAssets.body} alt="" />
                <span ref={headRef} className="interactive-mascot__head-layer">
                  <img className="interactive-mascot__layer" src={layeredAssets.head} alt="" />
                  {!activeEyes && <img className="interactive-mascot__layer" src={layeredAssets.eyeLeft} alt="" />}
                  {!activeEyes && <img className="interactive-mascot__layer" src={layeredAssets.eyeRight} alt="" />}
                  <img ref={pupilLeftRef} className={`interactive-mascot__layer interactive-mascot__pupil${pupilsVisible ? '' : ' is-hidden'}`} src={layeredAssets.pupilLeft} alt="" />
                  <img ref={pupilRightRef} className={`interactive-mascot__layer interactive-mascot__pupil${pupilsVisible ? '' : ' is-hidden'}`} src={layeredAssets.pupilRight} alt="" />
                  {activeEyes && <img className="interactive-mascot__layer interactive-mascot__expression" src={activeEyes} alt="" />}
                  {activeBrows && <img className="interactive-mascot__layer interactive-mascot__expression" src={activeBrows} alt="" />}
                  {activeMouth && <img className="interactive-mascot__layer interactive-mascot__expression" src={activeMouth} alt="" />}
                  {activeCheeks && <img className="interactive-mascot__layer interactive-mascot__expression" src={activeCheeks} alt="" />}
                  {activeEffect && <img className="interactive-mascot__layer interactive-mascot__effect" src={activeEffect} alt="" />}
                  <img className={`interactive-mascot__layer interactive-mascot__blink${blinking ? ' is-visible' : ''}`} src={activeBlink || layeredAssets.blink} alt="" />
                </span>
                <img className="interactive-mascot__layer interactive-mascot__cloud" src={layeredAssets.cloud} alt="" />
              </>
            ) : (
              <>
                <span className="interactive-mascot__fallback-body">
                  <img src={fallbackFrames.center} alt="" />
                </span>
                <span ref={headRef} className="interactive-mascot__fallback-head">
                  <img ref={headImageRef} className="interactive-mascot__fallback-frame is-active" src={fallbackFrames.center} alt="" />
                  <img ref={alternateHeadImageRef} className="interactive-mascot__fallback-frame" src={fallbackFrames.center} alt="" />
                  <span className={`interactive-mascot__fallback-blink is-left${blinking ? ' is-visible' : ''}`} />
                  <span className={`interactive-mascot__fallback-blink is-right${blinking ? ' is-visible' : ''}`} />
                </span>
              </>
            )}
          </span>
        </span>
      </button>
    </div>
  );
}
